import {Link, useNavigate, useLocation} from "react-router-dom"
import R from "react"
import * as P from "../program-state"
import {useForm, Controller, SubmitHandler} from "react-hook-form"
import {z} from "zod"


import {Eres_button, Eres_checkbox, Eres_form_error, Eres_form_label,
    Eres_form_toplabel, Eres_line_edit} from "../controls"

import {unreachable, use_program_state,
    login_redirect_to_target_in_query_param, login_begin_flow,
    last_attempt_failed, generation_counter_incr,
    login_handle_navigating_away_from_login_page,
    login_empty_failure, run_once_on_component_destruction} from "../program-state"
const _Login_page = () => {
    const program_state = use_program_state()
    const navigate = useNavigate()
    const location = useLocation()
    const sp = new URLSearchParams(location.search)
    const redirect_tos = sp.getAll("redirect-to")
    const redirect_to = redirect_tos.length === 1 ? redirect_tos[0] : null
    const session_expired = sp.get("session_expired") === "true"
    const ep = R.useRef({ email : "", pass : "" })
    const _frd = R.useRef(0)
    const [, _force_redraw] = R.useState(0)
    const force_redraw = R.useCallback(() => { ++_frd.current; _force_redraw(_frd.current) }, [_force_redraw])
    run_once_on_component_destruction(() => login_handle_navigating_away_from_login_page(program_state))
    const user_state = program_state.current
    const [form_disabled, set_form_disabled] = R.useState(true)
    type login_failed_record = { email_message : string, password_message : string, failure_reason : string }
    const [login_failed, set_login_failed] = R.useState(null as login_failed_record | null)
    R.useEffect(() => {
        switch (user_state.tag) {
        case "ps-unauthenticated": {
            set_form_disabled(false)
            const lf = user_state.last_failure
            switch (lf.tag) {
            case "no-attempts-yet": set_login_failed(null); break
            case "last-attempt-failed": {
                set_login_failed({
                    email_message : lf.email_message,
                    password_message : lf.password_message,
                    failure_reason : lf.generic_reason,
                })
                break
            }
            default: unreachable(lf)
            }
            break
        }
        case "ps-authenticating": set_form_disabled(true); break
        case "ps-authenticated": set_form_disabled(true); login_redirect_to_target_in_query_param(navigate, redirect_to); break
        default: unreachable(user_state)
        }
    }, [user_state, navigate, redirect_to])
    const handle_submit = (event : R.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const ct = event.currentTarget
        const len = ct.length
        let email : string = ""
        let password : string = ""
        for (let i = 0; i < len; ++i) {
            const elt = ct[i]
            if (elt instanceof HTMLInputElement) {
                switch (elt.type) {
                case "email": email = elt.value; break
                case "password": password = elt.value; break
                }
            }
        }
        let failure_description : last_attempt_failed | null = null
        if (email === "") {
            failure_description = { ...login_empty_failure, email_message : "Please enter your email address." }
        } else if (password === "") {
            failure_description = { ...login_empty_failure, password_message : "Please enter your password." }
        }
        ep.current = { email : email, pass : password }
        if (failure_description !== null) {
            program_state.current = { tag : "ps-unauthenticated", last_failure : failure_description }
            program_state.login_request_counter = generation_counter_incr(program_state.login_request_counter)
            force_redraw()
            return
        }
        void login_begin_flow(program_state, email, password, force_redraw, navigate, redirect_to)
    }
    const login_failed_style = login_failed ? { animation : "eres-shake 300ms" } : { }
    const Error_msg = ({msg} : {msg : string}) => msg !== "" ? <Eres_form_error msg={msg}/> : <></>
    return (
    <main
        className="tw-min-h-screen tw-w-full tw-m-0 tw-p-0 tw-flex tw-items-center tw-justify-center tw-bg-cover tw-bg-center tw-bg-no-repeat"
        style={{backgroundImage : "url(/eres-login.jpg)"}}
    >

        <div key={program_state.login_request_counter} className="tw-relative tw-z-10 tw-w-full tw-max-w-sm tw-mx-4 sm:tw-mx-auto" style={login_failed_style}>
            <div className="tw-bg-white tw-bg-opacity-20 tw-backdrop-blur-md tw-rounded-lg tw-shadow-2xl tw-p-8 tw-border tw-border-white tw-border-opacity-30">
                <h1 className="tw-font-bold tw-text-2xl tw-text-white tw-mb-6">Log in</h1>
                {session_expired && (
                    <div className="tw-mb-4 tw-p-3 tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded tw-text-sm tw-text-yellow-800">
                        Your session has expired. Please log in again.
                    </div>
                )}
                <form role="form" onSubmit={handle_submit}>
                <fieldset disabled={form_disabled}>
                    <div className="tw-mb-4">
                        <Eres_form_toplabel htmlFor="eml" className="!tw-text-white">Email</Eres_form_toplabel>
                        <Eres_line_edit id="eml" type="email" defaultValue={ep.current.email} aria-label="Email" className="tw-w-full tw-bg-white tw-bg-opacity-20 tw-text-white tw-border-white tw-border-opacity-30 placeholder:tw-text-gray-300"/>
                        <Error_msg msg={login_failed?.email_message || ""}/>
                    </div>
                    <div className="tw-mb-4">
                        <Eres_form_toplabel htmlFor="pw" className="!tw-text-white">Password</Eres_form_toplabel>
                        <Eres_line_edit id="pw" type="password" defaultValue={ep.current.pass} aria-label="Password" className="tw-w-full tw-bg-white tw-bg-opacity-20 tw-text-white tw-border-white tw-border-opacity-30 placeholder:tw-text-gray-300"/>
                        <Error_msg msg={login_failed?.password_message || ""}/>
                    </div>
                    <div className="tw-flex tw-items-center tw-mb-4">
                        <Eres_checkbox id="remember-me"/>
                        <Eres_form_label htmlFor="remember-me" className="tw-m-0 tw-ms-1.5 tw-text-sm tw-font-normal !tw-text-white">Remember Me</Eres_form_label>
                    </div>
                    <div>
                        <Eres_button type="submit" className="tw-w-full tw-mb-2">Log in</Eres_button>
                        <Error_msg msg={login_failed?.failure_reason || ""}/>
                    </div>
                </fieldset>
                </form>

                <p className="tw-text-center tw-mt-4 tw-text-sm tw-text-white">
                    Don't have an account? <Link to="/sign-up" className="tw-text-white tw-font-bold tw-hover:underline tw-underline">Sign up</Link>
                </p>
                <p className="tw-text-center tw-mt-2 tw-text-sm">
                    <Link to="/forgot-password" className="tw-text-white tw-font-bold tw-hover:underline tw-underline">Forgot password?</Link>
                </p>
            </div>
        </div>
    </main>
    )
}

export const Login_page = R.memo(_Login_page)

export const Logout_page = () => {
    const ps = P.use_program_state()
    const navigate = useNavigate()
    P.run_once_on_component_creation(() => {
        ps.token_store(null)
        ps.current = { tag : P.Unauthenticated, last_failure : { tag : P.No_attempts_yet } }
        navigate("/login", {replace: true})
    })
    return null
}


export const Sign_up_page = () => {
    type Sign_up_payload = {
        email: string
        invitation_code: string
        password: string
        password_confirm: string
    }
    const [error_message, set_error_message] = R.useState<string | null>(null)

    const { register, handleSubmit, watch, control, formState: { errors } } = useForm<Sign_up_payload>()
    const navigate = useNavigate()
    const onSubmit: SubmitHandler<Sign_up_payload> = async (data) => {
        const [ok, resp] = await P.issue_json_post(P.v1api`auth/register`, data, z.any(), undefined)
        if (ok) {
            console.log("Sign up success", resp)
            navigate("/login", {replace: true})
        } else {
            const r = resp as P.http_error
            const errbody = await r.resp.json()
            let error_message = ""
            switch (errbody.detail) {
                case "password-policy-failure": error_message = "Password does not meet the password policy requirements"; break
                case "email-in-use": error_message = "Email is already in use"; break
                case "password-confirmation-mismatch": error_message = "Passwords do not match"; break
                case "invitation-code-invalid": error_message = "Invalid invitation code"; break
                default: error_message = `An error occurred. Error: ${errbody.detail}`; break
            }
            set_error_message(error_message)
        }
    }

    watch(() => {
        set_error_message(null)
    })

    return (
        <main
            className="tw-min-h-screen tw-w-full tw-m-0 tw-p-0 tw-flex tw-items-center tw-justify-center tw-bg-cover tw-bg-center tw-bg-no-repeat"
            style={{backgroundImage : "url(/eres-login.jpg)"}}
        >
            <div className="tw-relative tw-z-10 tw-w-full tw-max-w-sm tw-mx-4 sm:tw-mx-auto">
                <div className="tw-bg-white tw-bg-opacity-20 tw-backdrop-blur-md tw-rounded-lg tw-shadow-2xl tw-p-8 tw-border tw-border-white tw-border-opacity-30">
                    <h1 className="tw-font-bold tw-text-2xl tw-text-white tw-mb-6">Sign up</h1>
                    <form role="form" onSubmit={handleSubmit(onSubmit)}>
                    <fieldset>
                        <div className="tw-mb-4">
                            <Eres_form_toplabel htmlFor="eml" className="!tw-text-white">Email</Eres_form_toplabel>
                            <Eres_line_edit id="eml" type="email" aria-label="Email" className="tw-w-full tw-bg-white tw-bg-opacity-20 tw-text-white tw-border-white tw-border-opacity-30 placeholder:tw-text-gray-300" extra={register("email", { required: 'Email is required' })}/>
                            {errors.email && <Eres_form_error msg={errors.email.message || ""}/> }
                        </div>
                        <div className="tw-mb-4">
                            <Eres_form_toplabel htmlFor="invite_code" className="!tw-text-white">Invitation Code</Eres_form_toplabel>
                            <Eres_line_edit id="invite_code" type="text" aria-label="Invitation" className="tw-w-full tw-bg-white tw-bg-opacity-20 tw-text-white tw-border-white tw-border-opacity-30 placeholder:tw-text-gray-300" extra={register("invitation_code", { required: 'Registration code is required' })}/>
                            {errors.invitation_code && <Eres_form_error msg={errors.invitation_code.message || ""}/> }
                        </div>
                        <div className="tw-mb-4">
                            <Eres_form_toplabel htmlFor="pw" className="!tw-text-white">Password</Eres_form_toplabel>
                            <Eres_line_edit id="pw" type="password" aria-label="Password" className="tw-w-full tw-bg-white tw-bg-opacity-20 tw-text-white tw-border-white tw-border-opacity-30 placeholder:tw-text-gray-300" extra={register("password", { required: 'Password is required' })} />
                            {errors.password && <Eres_form_error msg={errors.password.message || ""}/> }
                        </div>
                        <div className="tw-mb-4">
                            <Eres_form_toplabel htmlFor="pw-confirm" className="!tw-text-white">Confirm Password</Eres_form_toplabel>
                            <Eres_line_edit id="pw-confirm" type="password" aria-label="Password" className="tw-w-full tw-bg-white tw-bg-opacity-20 tw-text-white tw-border-white tw-border-opacity-30 placeholder:tw-text-gray-300"
                                extra={register("password_confirm", { required: 'Password confirmation is required', validate: value => value === watch('password') || 'The passwords do not match' })}
                            />
                            {errors.password_confirm && <Eres_form_error msg={errors.password_confirm.message || ""}/> }
                        </div>
                        <div>
                            <Eres_button type="submit" className="tw-w-full tw-mb-2">Sign up</Eres_button>
                            {error_message && <Eres_form_error msg={error_message || ""}/> }
                        </div>
                    </fieldset>
                    </form>

                    <p className="tw-text-center tw-mt-4 tw-text-sm tw-text-white">
                        Already have an account? <Link to="/login" className="tw-text-white tw-font-bold tw-hover:underline tw-underline">Login</Link>
                    </p>
                </div>
            </div>
        </main>
    )
}


export const Forgot_password_page = () => {
    type Forgot_password_payload = {
        email: string
    }
    const [error_message, set_error_message] = R.useState<string | null>(null)
    const { register, handleSubmit, watch, control, formState: { errors } } = useForm<Forgot_password_payload>()
    const navigate = useNavigate()
    const onSubmit: SubmitHandler<Forgot_password_payload> = async (data) => {
        const [ok, resp] = await P.issue_json_post(P.v1api`auth/password-recovery/`, data, z.any(), undefined)
        if (ok) {
            navigate("/login", {replace: true})
        } else {
            const r = resp as P.http_error
            const errbody = await r.resp.json()
            let error_message = `An error occurred. Error: ${errbody.detail}`
            set_error_message(error_message)
        }
    }

    watch(() => {
        set_error_message(null)
    })

    return (
        <main
            className="tw-min-h-screen tw-w-full tw-m-0 tw-p-0 tw-flex tw-items-center tw-justify-center tw-bg-cover tw-bg-center tw-bg-no-repeat"
            style={{backgroundImage : "url(/eres-login.jpg)"}}
        >
            <div className="tw-relative tw-z-10 tw-w-full tw-max-w-sm tw-mx-4 sm:tw-mx-auto">
                <div className="tw-bg-white tw-bg-opacity-20 tw-backdrop-blur-md tw-rounded-lg tw-shadow-2xl tw-p-8 tw-border tw-border-white tw-border-opacity-30">
                    <h1 className="tw-font-bold tw-text-2xl tw-text-white tw-mb-6">Request password reset</h1>
                    <form role="form" onSubmit={handleSubmit(onSubmit)}>
                    <fieldset>
                        <div className="tw-mb-4">
                            <Eres_form_toplabel htmlFor="eml" className="!tw-text-white">Email</Eres_form_toplabel>
                            <Eres_line_edit id="eml" type="email" aria-label="Email" className="tw-w-full tw-bg-white tw-bg-opacity-20 tw-text-white tw-border-white tw-border-opacity-30 placeholder:tw-text-gray-300" extra={register("email", {required: "Email is required"})}/>
                            {errors.email && <Eres_form_error msg={errors.email.message || ""}/> }
                        </div>
                        <div>
                            <Eres_button type="submit" className="tw-w-full tw-mb-2">Request password reset</Eres_button>
                            {error_message && <Eres_form_error msg={error_message || ""}/> }
                        </div>
                    </fieldset>
                    </form>

                    <p className="tw-text-center tw-mt-4 tw-text-sm">
                        <Link to="/" className="tw-text-white tw-font-bold tw-hover:underline tw-underline">Back to homepage</Link>
                    </p>
                </div>
            </div>
        </main>
    )
}


export const Reset_password_page = () => {
    const location = useLocation();
    const query_params = new URLSearchParams(location.search);
    const token = query_params.get('token');

    type Reset_password_payload = {
        password: string
        password_confirm: string
    }
    const [error_message, set_error_message] = R.useState<string | null>(null)
    const { register, handleSubmit, watch, control, formState: { errors } } = useForm<Reset_password_payload>()
    const navigate = useNavigate()
    const onSubmit: SubmitHandler<Reset_password_payload> = async (data) => {
        const [ok, resp] = await P.issue_json_post(P.v1api`auth/reset-password/`, {...data, token}, z.any(), undefined)
        if (ok) {
            navigate("/login", {replace: true})
        } else {
            const r = resp as P.http_error
            const errbody = await r.resp.json()
            let error_message = ""
            if (typeof errbody.detail === "string") {
                error_message = errbody.detail
            } else if (typeof errbody.detail === "object" && errbody.detail !== null) {
                if (errbody.detail.password) {
                    error_message = errbody.detail.password
                } else {
                    error_message = JSON.stringify(errbody.detail)
                }
            } else {
                error_message = "An error occurred"
            }
            set_error_message(error_message)
        }
    }

    watch(() => {
        set_error_message(null)
    })

    return (
        <main
            className="tw-min-h-screen tw-w-full tw-m-0 tw-p-0 tw-flex tw-items-center tw-justify-center tw-bg-cover tw-bg-center tw-bg-no-repeat"
            style={{backgroundImage : "url(/eres-login.jpg)"}}
        >
            <div className="tw-relative tw-z-10 tw-w-full tw-max-w-sm tw-mx-4 sm:tw-mx-auto">
                <div className="tw-bg-white tw-bg-opacity-20 tw-backdrop-blur-md tw-rounded-lg tw-shadow-2xl tw-p-8 tw-border tw-border-white tw-border-opacity-30">
                    <h1 className="tw-font-bold tw-text-2xl tw-text-white tw-mb-6">Reset your password</h1>
                    <form role="form" onSubmit={handleSubmit(onSubmit)}>
                    <fieldset>
                        <div className="tw-mb-4">
                            <Eres_form_toplabel htmlFor="pw" className="!tw-text-white">Enter your new password</Eres_form_toplabel>
                            <Eres_line_edit id="pw" type="password" aria-label="Password" className="tw-w-full tw-bg-white tw-bg-opacity-20 tw-text-white tw-border-white tw-border-opacity-30 placeholder:tw-text-gray-300" extra={register("password", { required: 'Password is required' })} />
                            {errors.password && <Eres_form_error msg={errors.password.message || ""}/> }
                        </div>
                        <div className="tw-mb-4">
                            <Eres_form_toplabel htmlFor="pw-confirm" className="!tw-text-white">Confirm your new password</Eres_form_toplabel>
                            <Eres_line_edit id="pw-confirm" type="password" aria-label="Password" className="tw-w-full tw-bg-white tw-bg-opacity-20 tw-text-white tw-border-white tw-border-opacity-30 placeholder:tw-text-gray-300"
                                extra={register("password_confirm", { required: 'Password confirmation is required', validate: value => value === watch('password') || 'The passwords do not match' })}
                            />
                            {errors.password_confirm && <Eres_form_error msg={errors.password_confirm.message || ""}/> }
                        </div>
                        <div>
                            <Eres_button type="submit" className="tw-w-full tw-mb-2">Update password</Eres_button>
                            {error_message && <Eres_form_error msg={error_message || ""}/> }
                        </div>
                    </fieldset>
                    </form>

                    <p className="tw-text-center tw-mt-4 tw-text-sm">
                        <Link to="/" className="tw-text-white tw-font-bold tw-hover:underline tw-underline">Back to homepage</Link>
                    </p>
                </div>
            </div>
        </main>
    )
}
